import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { MIXPANEL_CONFIG } from './mixpanel.config';

/** Response of the GDPR service GET /gdpr/opt_out. */
export interface TrackingOptOutResponse {
  region: string;
  opt_out: boolean;
}

/**
 * Stores JSON {@link CachedOptOut}. Only successful backend answers are stored, tied to the
 * hashed identity (never the plain e-mail) so another user on the same browser is asked again.
 */
export const TRACKING_OPT_OUT_KEY = 'dd_tracking_opt_out';

/** Persisted opt-out answer for one identity (id = SHA-256 hex of the e-mail). */
export interface CachedOptOut {
  id: string;
  optOut: boolean;
}

/**
 * GDPR opt-out check for Mixpanel. Fail-closed: if the endpoint fails (or answers
 * something unexpected) tracking is blocked for this page session, and that result is
 * NOT persisted so the next page load asks the backend again.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  /**
   * Bypasses the app interceptors: the GDPR service needs no auth, and its CORS preflight
   * rejects the Authorization header that authInterceptor would add. It also keeps a 401
   * here from triggering the SSO redirect in requestInterceptor.
   */
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly config = inject(MIXPANEL_CONFIG);
  private readonly optedOut = signal(false);
  private check$?: Observable<boolean>;
  /** Identity the shared check$ belongs to. */
  private checkId: string | null = null;

  readonly isOptedOut = this.optedOut.asReadonly();

  /**
   * Persisted backend answer for this identity, to skip the HTTP call on reload.
   * null = unknown (no identity, nothing stored, or stored for a different identity).
   */
  getCachedOptOut(id: string | null): boolean | null {
    if (!id) return null;
    try {
      const raw = localStorage.getItem(TRACKING_OPT_OUT_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw) as Partial<CachedOptOut> | null;
      if (cached?.id !== id || typeof cached.optOut !== 'boolean') return null;
      return cached.optOut;
    } catch {
      return null;
    }
  }

  /**
   * Emits true when the user opted out (or the check failed), false when tracking is allowed.
   * `id` is the hashed identity (SHA-256 hex) the answer belongs to; without it the cache is
   * neither read nor written. The result is shared per identity until reset(). No
   * finalize()/"checked" flag is used, so subscribers can never re-enter the check from their
   * callback (the shareReplay loop in the skill notes).
   */
  checkOptOut(id: string | null): Observable<boolean> {
    if (this.check$ && this.checkId === id) return this.check$;
    this.checkId = id;

    const cached = this.getCachedOptOut(id);
    if (cached !== null) {
      this.optedOut.set(cached);
      this.check$ = of(cached);
      return this.check$;
    }

    this.check$ = this.http.get<TrackingOptOutResponse>(this.config.optOutUrl).pipe(
      map((response) => {
        if (typeof response?.opt_out !== 'boolean') {
          throw new Error('Invalid tracking opt-out response');
        }
        return response.opt_out;
      }),
      tap((optOut) => this.persist(id, optOut)),
      // Fail-closed for this page session only (not persisted).
      catchError(() => of(true)),
      tap((optOut) => this.optedOut.set(optOut)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.check$;
  }

  /** Call on logout so the next login checks again. */
  reset(): void {
    this.check$ = undefined;
    this.checkId = null;
    this.optedOut.set(false);
    try {
      localStorage.removeItem(TRACKING_OPT_OUT_KEY);
    } catch {
      // Storage unavailable: nothing to clear.
    }
  }

  private persist(id: string | null, optOut: boolean): void {
    if (!id) return;
    try {
      const value: CachedOptOut = { id, optOut };
      localStorage.setItem(TRACKING_OPT_OUT_KEY, JSON.stringify(value));
    } catch {
      // Storage unavailable: the check simply runs again next load.
    }
  }
}
